import React, { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Lock, Mail, User, AlertCircle, CheckCircle } from "lucide-react";
import LanguageSelector from "../components/LanguageSelector";
import axios from "axios";
import i18n from "../i18n";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_telenot-portal/artifacts/5ccwvbhz_PLU_Logo_Claim_RGB.png";

const RegisterPage = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const invitationToken = searchParams.get("token");
  const langParam = searchParams.get("lang");
  
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [name, setName] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isValidating, setIsValidating] = useState(true);
  const [invitation, setInvitation] = useState(null);
  const [error, setError] = useState(null);
  
  const { register } = useAuth();

  // Set language from URL parameter on mount
  useEffect(() => {
    if (langParam) {
      i18n.changeLanguage(langParam);
      // Set RTL for Arabic
      if (langParam === "ar") {
        document.documentElement.dir = "rtl";
      } else {
        document.documentElement.dir = "ltr";
      }
    }
  }, [langParam]);

  useEffect(() => {
    const validateToken = async () => {
      if (!invitationToken) {
        setError(t("register.noToken"));
        setIsValidating(false);
        return;
      }

      try {
        const response = await axios.get(
          `${API_URL}/api/auth/invitation/${invitationToken}`
        );
        setInvitation(response.data);
        setEmail(response.data.customer_email);
        setName(response.data.customer_name);
        
        // Set language from project if not already set by URL param
        if (!langParam && response.data.language) {
          i18n.changeLanguage(response.data.language);
          if (response.data.language === "ar") {
            document.documentElement.dir = "rtl";
          }
        }
      } catch (err) {
        setError(t("register.invalidToken"));
      } finally {
        setIsValidating(false);
      }
    };

    validateToken();
  }, [invitationToken, langParam, t]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast.error(t("register.passwordMismatch"));
      return;
    }

    if (password.length < 6) {
      toast.error(t("register.passwordTooShort"));
      return;
    }

    setIsLoading(true);

    try {
      await register(email, password, name, invitationToken);
      toast.success(t("register.registrationSuccess"));
    } catch (error) {
      const message = error.response?.data?.detail || t("register.registrationFailed");
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  if (isValidating) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#1e3a5f] dark:border-blue-400 mx-auto mb-4"></div>
          <p className="text-slate-600 dark:text-muted-foreground">{t("register.validating")}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-background flex items-center justify-center p-4">
        <Card className="card-base max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <div className="w-16 h-16 bg-red-100 dark:bg-red-950 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-red-600 dark:text-red-400" />
            </div>
            <h2 className="font-manrope text-xl font-bold text-slate-900 dark:text-foreground mb-2">
              {t("register.invalidTokenTitle")}
            </h2>
            <p className="text-slate-500 dark:text-muted-foreground mb-6">{error}</p>
            <Link to="/login">
              <Button className="bg-[#1e3a5f] hover:bg-[#0d2137] dark:bg-blue-600 dark:hover:bg-blue-700">
                {t("register.toLogin")}
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-background flex">
      {/* Language Selector - Top Right */}
      <div className="absolute top-4 right-4 z-50">
        <LanguageSelector className="bg-white/90 dark:bg-card backdrop-blur-sm" />
      </div>

      {/* Left Side - Branding with technical image */}
      <div 
        className="hidden lg:flex lg:w-1/2 relative overflow-hidden"
      >
        <div 
          className="absolute inset-0"
          style={{
            backgroundImage: "url('https://images.unsplash.com/photo-1518770660439-4636190af475?auto=format&fit=crop&q=80&w=1920')",
            backgroundSize: "cover",
            backgroundPosition: "center"
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900/95 via-slate-900/90 to-slate-800/85" />
        <div className="relative z-10 flex flex-col justify-center px-12 text-white">
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 mb-8 inline-block">
            <img 
              src={LOGO_URL} 
              alt="plutec" 
              className="w-64"
            />
          </div>
          <h1 className="font-manrope font-bold text-4xl leading-tight mb-4">
            {t("register.welcome")}
          </h1>
          <p className="text-slate-300 text-lg max-w-md">
            {t("register.welcomeSubtitle")}
          </p>
          <div className="mt-8 flex items-center gap-4">
            <div className="w-12 h-1 bg-blue-500 rounded"></div>
            <span className="text-sm text-slate-400">{t('branding.tagline', 'Sichere Übertragung. Zuverlässige Alarmierung.')}</span>
          </div>
        </div>
      </div>

      {/* Right Side - Register Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden flex items-center justify-center mb-8">
            <img 
              src={LOGO_URL} 
              alt="plutec" 
              className="w-48"
            />
          </div>

          {/* Project Info */}
          {invitation && (
            <div className="bg-emerald-50 dark:bg-emerald-950/50 border border-emerald-200 dark:border-emerald-800 rounded-lg p-4 mb-6 flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-emerald-600 dark:text-emerald-400 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-emerald-800 dark:text-emerald-300">
                  {t("register.projectInvitation")}
                </p>
                <p className="text-emerald-700 dark:text-emerald-400 font-semibold">
                  {invitation.project_name}
                </p>
              </div>
            </div>
          )}

          <Card className="card-base" data-testid="register-card">
            <CardHeader className="space-y-1 pb-6">
              <CardTitle className="font-manrope text-2xl font-bold text-slate-900 dark:text-foreground">
                {t("register.title")}
              </CardTitle>
              <CardDescription className="text-slate-500 dark:text-muted-foreground">
                {t("register.subtitle")}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name" className="label-style">
                    {t("common.name")}
                  </Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 dark:text-muted-foreground" />
                    <Input
                      id="name"
                      type="text"
                      placeholder={t("register.yourName")}
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="pl-10"
                      required
                      data-testid="register-name-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email" className="label-style">
                    {t("common.email")}
                  </Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 dark:text-muted-foreground" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="name@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-10 bg-slate-50 dark:bg-muted"
                      required
                      disabled
                      data-testid="register-email-input"
                    />
                  </div>
                  <p className="text-xs text-slate-500 dark:text-muted-foreground">
                    {t("register.emailFromInvitation")}
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password" className="label-style">
                    {t("common.password")}
                  </Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 dark:text-muted-foreground" />
                    <Input
                      id="password"
                      type="password"
                      placeholder={t("register.minPassword")}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="pl-10"
                      required
                      data-testid="register-password-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword" className="label-style">
                    {t("auth.confirmPassword")}
                  </Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 dark:text-muted-foreground" />
                    <Input
                      id="confirmPassword"
                      type="password"
                      placeholder={t("register.repeatPassword")}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="pl-10"
                      required
                      data-testid="register-confirm-password-input"
                    />
                  </div>
                </div>
                <Button
                  type="submit"
                  className="w-full bg-[#1e3a5f] hover:bg-[#0d2137] dark:bg-blue-600 dark:hover:bg-blue-700 text-white h-11 mt-2"
                  disabled={isLoading}
                  data-testid="register-submit-btn"
                >
                  {isLoading ? t("common.loading") : t("register.registerButton")}
                </Button>
              </form>

              <div className="mt-6 text-center text-sm text-slate-500 dark:text-muted-foreground">
                {t("register.alreadyAccount")}{" "}
                <Link
                  to="/login"
                  className="text-[#1e3a5f] dark:text-blue-400 hover:underline font-medium"
                >
                  {t("register.loginLink")}
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
